import React, { CSSProperties, ReactNode, useMemo, useState } from 'react';
import DialogBase from '../../../base/DialogBase/DialogBase';
import styles from './HealthCheckDetailDialog.module.css';
import { ClipboardCopy, ClipboardList, CheckSquare, Search } from 'lucide-react';

type HealthCheckDetailDialogProps = {
    onClose: () => void;
    visible: boolean;
    title: string;
    additionalData: string;
    style?: CSSProperties | undefined;
    children?: ReactNode | undefined;
};

/** Parse a single AdditionalData line into { id, rest } */
const parseLine = (line: string): { id: string; rest: string } => {
    const trimmed = line.trim();
    if (!trimmed) return { id: '', rest: '' };
    const sepIdx = trimmed.indexOf(' : ');
    if (sepIdx === -1) return { id: trimmed, rest: '' };
    return { id: trimmed.substring(0, sepIdx), rest: trimmed.substring(sepIdx + 3) };
};

const HealthCheckDetailDialog = (props: HealthCheckDetailDialogProps) => {
    const { style, visible, title, additionalData, onClose } = props;
    const [selectedRows, setSelectedRows] = useState<Set<number>>(new Set());
    const [searchText, setSearchText] = useState('');
    const [copied, setCopied] = useState<string | null>(null);

    /** Parsed rows from the newline-delimited AdditionalData string */
    const rows = useMemo(() => {
        if (!additionalData) return [];
        return additionalData
            .split('\n')
            .map(l => l.trim())
            .filter(l => l.length > 0)
            .map(parseLine);
    }, [additionalData]);

    /** Filtered rows based on search */
    const filteredRows = useMemo(() => {
        if (!searchText) return rows.map((r, i) => ({ ...r, originalIndex: i }));
        const lower = searchText.toLowerCase();
        return rows
            .map((r, i) => ({ ...r, originalIndex: i }))
            .filter(r => r.id.toLowerCase().includes(lower) || r.rest.toLowerCase().includes(lower));
    }, [rows, searchText]);

    /** Flash a "Copied!" indicator then reset */
    const flashCopied = (label: string) => {
        setCopied(label);
        setTimeout(() => setCopied(null), 1500);
    };

    /** Copy the full AdditionalData text */
    const handleCopyAll = () => {
        navigator.clipboard.writeText(additionalData).then(() => flashCopied('all'));
    };

    /** Copy only the IDs column (all rows) */
    const handleCopyAllIds = () => {
        const ids = rows.map(r => r.id).join('\n');
        navigator.clipboard.writeText(ids).then(() => flashCopied('ids'));
    };

    /** Copy only the IDs of selected rows */
    const handleCopySelectedIds = () => {
        const ids = rows.filter((_, i) => selectedRows.has(i)).map(r => r.id).join('\n');
        navigator.clipboard.writeText(ids).then(() => flashCopied('selected'));
    };

    /** Toggle a row's selection */
    const toggleRow = (originalIndex: number) => {
        setSelectedRows(prev => {
            const next = new Set(prev);
            if (next.has(originalIndex)) next.delete(originalIndex);
            else next.add(originalIndex);
            return next;
        });
    };

    /** Toggle all visible rows */
    const toggleAll = () => {
        const visibleIndices = filteredRows.map(r => r.originalIndex);
        const allSelected = visibleIndices.every(i => selectedRows.has(i));
        setSelectedRows(prev => {
            const next = new Set(prev);
            if (allSelected) visibleIndices.forEach(i => next.delete(i));
            else visibleIndices.forEach(i => next.add(i));
            return next;
        });
    };

    const allVisibleSelected = filteredRows.length > 0 && filteredRows.every(r => selectedRows.has(r.originalIndex));

    const actions = (
        <div className="flex items-center gap-2 w-full">
            {/* Copy selected IDs */}
            <button
                className="btn btn-secondary text-xs"
                onClick={handleCopySelectedIds}
                disabled={selectedRows.size === 0}
                title="Copy selected element IDs to clipboard"
            >
                <CheckSquare size={14} className="mr-1" />
                {copied === 'selected' ? 'Copied!' : `Copy Selected IDs (${selectedRows.size})`}
            </button>

            {/* Copy all IDs */}
            <button
                className="btn btn-secondary text-xs"
                onClick={handleCopyAllIds}
                title="Copy all element IDs to clipboard"
            >
                <ClipboardList size={14} className="mr-1" />
                {copied === 'ids' ? 'Copied!' : 'Copy All IDs'}
            </button>

            {/* Spacer */}
            <div className="flex-1" />

            {/* Copy all */}
            <button
                className="btn btn-primary text-xs"
                onClick={handleCopyAll}
                title="Copy full details to clipboard"
            >
                <ClipboardCopy size={14} className="mr-1" />
                {copied === 'all' ? 'Copied!' : 'Copy All'}
            </button>
        </div>
    );

    return (
        <DialogBase
            title={`${title} — Details`}
            type="Custom"
            visible={visible}
            onClose={onClose}
            actions={actions}
            width="720px"
            className={styles.HealthCheckDetailDialog}
            style={style}
            data-testid="HealthCheckDetailDialog"
        >
            {rows.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-10 gap-2">
                    <span className="text-sm" style={{ color: 'var(--text-tertiary)' }}>
                        No additional data available for this health check.
                    </span>
                </div>
            ) : (
                <div className="flex flex-col gap-3">
                    {/* Search bar + row count */}
                    <div className="flex items-center gap-3">
                        <div className="relative flex-1">
                            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: 'var(--text-tertiary)' }} />
                            <input
                                type="text"
                                placeholder="Search…"
                                className="input-base pl-9 w-full"
                                value={searchText}
                                onChange={e => setSearchText(e.target.value)}
                            />
                        </div>
                        <span className="text-xs whitespace-nowrap" style={{ color: 'var(--text-tertiary)' }}>
                            {filteredRows.length === rows.length
                                ? `${rows.length} items`
                                : `${filteredRows.length} of ${rows.length} items`}
                        </span>
                    </div>

                    {/* Table */}
                    <div
                        className="rounded-lg border overflow-auto"
                        style={{
                            borderColor: 'var(--border-default)',
                            maxHeight: '400px',
                        }}
                    >
                        <table className="w-full text-sm" style={{ borderCollapse: 'collapse' }}>
                            <thead className="sticky top-0 z-10" style={{ backgroundColor: 'var(--surface-50)' }}>
                                <tr style={{ borderBottom: '1px solid var(--border-default)' }}>
                                    <th className="px-3 py-2 w-8 text-center">
                                        <input
                                            type="checkbox"
                                            checked={allVisibleSelected}
                                            onChange={toggleAll}
                                            className="accent-[var(--accent)]"
                                            title="Select all"
                                        />
                                    </th>
                                    <th
                                        className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wider"
                                        style={{ color: 'var(--text-tertiary)' }}
                                    >
                                        Element ID
                                    </th>
                                    <th
                                        className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wider"
                                        style={{ color: 'var(--text-tertiary)' }}
                                    >
                                        Details
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredRows.map(row => (
                                    <tr
                                        key={row.originalIndex}
                                        className="transition-colors cursor-pointer"
                                        style={{ borderBottom: '1px solid var(--border-subtle)' }}
                                        onClick={() => toggleRow(row.originalIndex)}
                                        onMouseEnter={e => (e.currentTarget.style.backgroundColor = 'var(--bg-hover)')}
                                        onMouseLeave={e => (e.currentTarget.style.backgroundColor = selectedRows.has(row.originalIndex) ? 'var(--surface-100, var(--bg-hover))' : 'transparent')}
                                    >
                                        <td className="px-3 py-1.5 text-center">
                                            <input
                                                type="checkbox"
                                                checked={selectedRows.has(row.originalIndex)}
                                                onChange={() => toggleRow(row.originalIndex)}
                                                onClick={e => e.stopPropagation()}
                                                className="accent-[var(--accent)]"
                                            />
                                        </td>
                                        <td
                                            className="px-3 py-1.5 font-mono text-xs"
                                            style={{ color: 'var(--text-primary)', whiteSpace: 'nowrap' }}
                                        >
                                            {row.id}
                                        </td>
                                        <td className="px-3 py-1.5" style={{ color: 'var(--text-secondary)' }}>
                                            {row.rest || <span style={{ color: 'var(--text-tertiary)' }}>—</span>}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>

                        {filteredRows.length === 0 && (
                            <div className="flex items-center justify-center py-6">
                                <span className="text-sm" style={{ color: 'var(--text-tertiary)' }}>No matching items</span>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </DialogBase>
    );
};

export default HealthCheckDetailDialog;
